import React, { useMemo, useState } from "react";
import { Button, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { CodeOutlined, DownloadOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import Papa from "papaparse";
import type { QueryCell, QueryResult } from "../../store/api/dataQuestionsApi";
import styles from "./styles.module.css";

interface ResultTableProps {
  result: QueryResult;
  /** column name -> route template with `{id}` (from the catalog). */
  links: Record<string, string>;
  sql: string | null;
  /** Used to name the CSV file. */
  title?: string;
}

const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const humanize = (column: string): string =>
  column.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

const isIdColumn = (column: string): boolean => /(^|_)id$/.test(column);

export const formatCell = (value: QueryCell, column: string): React.ReactNode => {
  if (value === null || value === undefined) {
    return <Typography.Text type="secondary">—</Typography.Text>;
  }
  if (typeof value === "boolean") return value ? "Sí" : "No";
  if (typeof value === "number") {
    if (isIdColumn(column)) return String(value);
    return Number.isInteger(value)
      ? value.toLocaleString("es-MX")
      : value.toLocaleString("es-MX", { maximumFractionDigits: 2 });
  }
  if (ISO_TIMESTAMP.test(value)) {
    const parsed = dayjs(value);
    if (parsed.isValid()) return parsed.format("DD/MM/YYYY HH:mm");
  }
  if (ISO_DATE.test(value)) {
    const parsed = dayjs(value);
    if (parsed.isValid()) return parsed.format("DD/MM/YYYY");
  }
  return value;
};

const linkFor = (
  links: Record<string, string>,
  column: string,
  value: QueryCell
): string | null => {
  const template = links[column];
  if (!template || value === null || value === undefined) return null;
  if (typeof value !== "number" && typeof value !== "string") return null;
  return template.replace("{id}", encodeURIComponent(String(value)));
};

const slug = (text: string): string =>
  text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 60) || "consulta";

const downloadCsv = (result: QueryResult, title: string) => {
  const csv = Papa.unparse(result.rows, { columns: result.columns });
  const blob = new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${slug(title)}-${dayjs().format("YYYYMMDD-HHmm")}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const rowsSummary = (result: QueryResult): string => {
  const shown = result.rowCount.toLocaleString("es-MX");
  if (result.truncated) {
    // totalCount is null when the separate count(*) failed: say the list is
    // cut without inventing a total.
    return result.totalCount === null
      ? `Mostrando las primeras ${shown} filas (hay más)`
      : `${result.totalCount.toLocaleString("es-MX")} filas en total, mostrando las primeras ${shown}`;
  }
  return `${shown} ${result.rowCount === 1 ? "fila" : "filas"}`;
};

/**
 * The rows the SQL returned, with `*_id` cells turned into back-office links
 * so a finding can be acted on in two clicks, plus CSV export and the SQL
 * behind a toggle for whoever wants to audit it.
 */
export const ResultTable: React.FC<ResultTableProps> = ({
  result,
  links,
  sql,
  title = "consulta",
}) => {
  const [showSql, setShowSql] = useState(false);

  const columns = useMemo<ColumnsType<Record<string, QueryCell>>>(
    () =>
      result.columns.map((column) => ({
        title: humanize(column),
        dataIndex: column,
        key: column,
        ellipsis: true,
        render: (value: QueryCell) => {
          const href = linkFor(links, column, value);
          const formatted = formatCell(value, column);
          return href ? <Link to={href}>{formatted}</Link> : formatted;
        },
      })),
    [result.columns, links]
  );

  const dataSource = useMemo(
    () => result.rows.map((row, index) => ({ ...row, __key: index })),
    [result.rows]
  );

  return (
    <div className={styles.result} data-testid="result-table">
      <div className={styles.resultHeader}>
        <Typography.Text type="secondary">
          {rowsSummary(result)} · {result.durationMs} ms
        </Typography.Text>
        <span className={styles.spacer} />
        {sql && (
          <Button
            size="small"
            type={showSql ? "primary" : "default"}
            icon={<CodeOutlined />}
            onClick={() => setShowSql((value) => !value)}
          >
            {showSql ? "Ocultar SQL" : "Ver SQL"}
          </Button>
        )}
        <Button
          size="small"
          icon={<DownloadOutlined />}
          disabled={result.rowCount === 0}
          onClick={() => downloadCsv(result, title)}
        >
          Exportar CSV
        </Button>
      </div>
      {showSql && sql && (
        <div className={styles.resultSql}>
          <pre className={styles.sql}>{sql}</pre>
        </div>
      )}
      <Table
        size="small"
        columns={columns}
        dataSource={dataSource}
        rowKey="__key"
        locale={{ emptyText: "La consulta no devolvió filas" }}
        pagination={
          result.rowCount > 25
            ? { pageSize: 25, size: "small", showSizeChanger: false }
            : false
        }
        scroll={{ x: true }}
      />
    </div>
  );
};
